import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Calendar, Clock, Flame, Plus, Trash2, Users, Tag, AlertTriangle,
  CheckCircle2, ListTodo, Loader2, X, ChevronRight, Sparkles,
  Building2, Edit, Search, Filter,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// === KANBAN CONFIG ===
const COLUMNS = [
  { id: 'todo', label: 'Da Fare', icon: ListTodo, accent: 'from-slate-400 to-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' },
  { id: 'in_progress', label: 'In Corso', icon: Loader2, accent: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50', border: 'border-blue-200' },
  { id: 'in_attesa', label: 'In Attesa', icon: Clock, accent: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', border: 'border-amber-200' },
  { id: 'done', label: 'Completati', icon: CheckCircle2, accent: 'from-emerald-500 to-green-500', bg: 'bg-emerald-50', border: 'border-emerald-200' },
];

const PRIORITA = {
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500', icon: Flame },
  alta:    { label: 'Alta',    color: 'bg-orange-100 text-orange-800 border-orange-300', dot: 'bg-orange-500', icon: AlertTriangle },
  media:   { label: 'Media',   color: 'bg-blue-100 text-blue-800 border-blue-300', dot: 'bg-blue-500', icon: Clock },
  bassa:   { label: 'Bassa',   color: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500', icon: CheckCircle2 },
};

const emptyForm = {
  titolo: '', descrizione: '',
  cliente_id: '', cliente_nome: '',
  tipo_lavoro: '', priorita: 'media',
  data_inizio: new Date().toISOString().slice(0,10),
  data_scadenza: '',
  assegnato_a_id: '', assegnato_a_nome: '',
  tag: [],
  note: '',
};

// === MAIN PAGE ===
export default function CalendarioImpegniPage() {
  const { user } = useAuth();
  const [impegni, setImpegni] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, urgenti_attivi: 0, scaduti: 0, miei_attivi: 0, totale: 0 });
  const [aziende, setAziende] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [search, setSearch] = useState('');
  const [filterPriorita, setFilterPriorita] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [iRes, sRes, aRes, uRes] = await Promise.all([
        api.get('/studio/calendario'),
        api.get('/studio/calendario/stats'),
        api.get('/aziende').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setImpegni(iRes.data || []);
      setStats(sRes.data || stats);
      setAziende(Array.isArray(aRes.data) ? aRes.data : (aRes.data?.items || []));
      setUsers(Array.isArray(uRes.data) ? uRes.data : (uRes.data?.items || []));
    } catch (e) {
      toast.error('Errore caricamento impegni');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // === Filtering ===
  const filtered = useMemo(() => {
    return impegni.filter(i => {
      if (search) {
        const q = search.toLowerCase();
        const hit = (i.titolo || '').toLowerCase().includes(q) ||
                    (i.cliente_nome || '').toLowerCase().includes(q) ||
                    (i.tipo_lavoro || '').toLowerCase().includes(q) ||
                    (i.tag || []).some(t => t.toLowerCase().includes(q));
        if (!hit) return false;
      }
      if (filterPriorita && i.priorita !== filterPriorita) return false;
      return true;
    });
  }, [impegni, search, filterPriorita]);

  const byCol = useMemo(() => {
    const map = { todo: [], in_progress: [], in_attesa: [], done: [] };
    filtered.forEach(i => { (map[i.status] || map.todo).push(i); });
    return map;
  }, [filtered]);

  // === Drag & drop ===
  const onDragStart = (id) => setDraggedId(id);
  const onDragEnd = () => { setDraggedId(null); setHoverCol(null); };
  const onDragOver = (e, col) => { e.preventDefault(); setHoverCol(col); };
  const onDrop = async (e, col) => {
    e.preventDefault();
    setHoverCol(null);
    if (!draggedId) return;
    const imp = impegni.find(i => i.id === draggedId);
    if (!imp || imp.status === col) { setDraggedId(null); return; }
    // optimistic update
    setImpegni(arr => arr.map(i => i.id === draggedId ? { ...i, status: col } : i));
    setDraggedId(null);
    try {
      await api.patch(`/studio/calendario/${draggedId}/status`, { status: col });
      if (col === 'done') {
        triggerConfetti();
        toast.success('🎉 Impegno completato!');
      }
      // refresh stats
      api.get('/studio/calendario/stats').then(r => setStats(r.data));
    } catch (err) {
      toast.error('Errore cambio stato');
      load();
    }
  };

  const triggerConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 2400);
  };

  // === CRUD ===
  const openNew = () => { setEditingId(null); setForm({ ...emptyForm, assegnato_a_id: user?.id || '', assegnato_a_nome: user?.full_name || '' }); setOpenDialog(true); };
  const openEdit = (imp) => {
    setEditingId(imp.id);
    setForm({
      titolo: imp.titolo || '',
      descrizione: imp.descrizione || '',
      cliente_id: imp.cliente_id || '',
      cliente_nome: imp.cliente_nome || '',
      tipo_lavoro: imp.tipo_lavoro || '',
      priorita: imp.priorita || 'media',
      data_inizio: imp.data_inizio || '',
      data_scadenza: imp.data_scadenza || '',
      assegnato_a_id: imp.assegnato_a_id || '',
      assegnato_a_nome: imp.assegnato_a_nome || '',
      tag: imp.tag || [],
      note: imp.note || '',
    });
    setOpenDialog(true);
  };
  const submit = async () => {
    if (!form.titolo.trim()) { toast.error('Titolo obbligatorio'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/studio/calendario/${editingId}`, form);
        toast.success('Impegno aggiornato');
      } else {
        await api.post('/studio/calendario', form);
        toast.success('🚀 Nuovo impegno creato');
      }
      setOpenDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };
  const deleteImpegno = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Eliminare questo impegno?')) return;
    try {
      await api.delete(`/studio/calendario/${id}`);
      setImpegni(arr => arr.filter(i => i.id !== id));
      toast.success('Eliminato');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const onSelectCliente = (cliId) => {
    const c = aziende.find(a => (a.id || a._id) === cliId);
    setForm(f => ({ ...f, cliente_id: cliId, cliente_nome: c ? (c.denominazione || c.ragione_sociale || '') : '' }));
  };
  const onSelectAssegnato = (uid) => {
    const u = users.find(x => (x.id || x._id) === uid);
    setForm(f => ({ ...f, assegnato_a_id: uid, assegnato_a_nome: u ? (u.full_name || u.email || '') : '' }));
  };
  const addTag = () => {
    const t = tagInput.trim();
    if (!t || form.tag.includes(t)) return;
    setForm(f => ({ ...f, tag: [...f.tag, t] }));
    setTagInput('');
  };
  const removeTag = (t) => setForm(f => ({ ...f, tag: f.tag.filter(x => x !== t) }));

  return (
    <div className="space-y-4 relative" data-testid="calendario-impegni-page">
      {/* Confetti effect */}
      <AnimatePresence>
        {showConfetti && (
          <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
            {[...Array(40)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ x: window.innerWidth / 2, y: -20, rotate: 0, opacity: 1 }}
                animate={{
                  x: Math.random() * window.innerWidth,
                  y: window.innerHeight + 50,
                  rotate: Math.random() * 720,
                  opacity: 0,
                }}
                transition={{ duration: 2 + Math.random(), ease: 'easeOut' }}
                className={`absolute w-3 h-3 rounded-sm ${['bg-emerald-500','bg-amber-400','bg-rose-400','bg-sky-400','bg-violet-500'][i%5]}`}
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Calendar className="w-8 h-8 text-violet-600" /> Calendario Impegni
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Pianifica e priorizza i lavori dello studio. Trascina le card tra le colonne per cambiarne lo stato.
          </p>
        </div>
        <Button onClick={openNew} className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shadow-md" data-testid="new-impegno-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo Impegno
        </Button>
      </motion.div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Sparkles} label="Totale" value={stats.totale} accent="text-violet-600 bg-violet-50" />
        <StatCard icon={Flame} label="Urgenti attivi" value={stats.urgenti_attivi} accent="text-red-600 bg-red-50" pulse={stats.urgenti_attivi > 0} />
        <StatCard icon={AlertTriangle} label="Scaduti" value={stats.scaduti} accent="text-amber-600 bg-amber-50" pulse={stats.scaduti > 0} />
        <StatCard icon={Users} label="Miei attivi" value={stats.miei_attivi} accent="text-blue-600 bg-blue-50" />
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-2 items-center bg-white border rounded-lg p-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca per titolo, cliente, tag..."
            className="pl-8 border-0 shadow-none focus-visible:ring-1" data-testid="search-input" />
        </div>
        <div className="flex items-center gap-1 text-xs">
          <Filter className="w-4 h-4 text-slate-400" />
          <button onClick={() => setFilterPriorita('')}
            className={`px-2 py-1 rounded ${!filterPriorita ? 'bg-slate-200' : 'hover:bg-slate-100'}`}>Tutte</button>
          {Object.entries(PRIORITA).map(([k, v]) => (
            <button key={k} onClick={() => setFilterPriorita(filterPriorita === k ? '' : k)}
              className={`px-2 py-1 rounded flex items-center gap-1 ${filterPriorita === k ? v.color : 'hover:bg-slate-100'}`}
              data-testid={`filter-${k}`}>
              <span className={`w-2 h-2 rounded-full ${v.dot}`} />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Kanban */}
      {loading ? (
        <div className="text-center py-12 text-slate-500"><Loader2 className="w-8 h-8 mx-auto animate-spin mb-2" /> Caricamento...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {COLUMNS.map(col => {
            const items = byCol[col.id] || [];
            const Icon = col.icon;
            const isHover = hoverCol === col.id;
            return (
              <div key={col.id}
                onDragOver={(e) => onDragOver(e, col.id)}
                onDrop={(e) => onDrop(e, col.id)}
                onDragLeave={() => setHoverCol(null)}
                className={`rounded-xl ${col.bg} ${col.border} border-2 ${isHover ? 'ring-2 ring-violet-400 scale-[1.02]' : ''} transition-all duration-200`}
                data-testid={`col-${col.id}`}
              >
                {/* Header colonna */}
                <div className={`bg-gradient-to-r ${col.accent} text-white p-3 rounded-t-xl flex justify-between items-center`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${col.id === 'in_progress' ? 'animate-spin' : ''}`} />
                    <strong className="text-sm">{col.label}</strong>
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full" data-testid={`col-count-${col.id}`}>
                    {items.length}
                  </span>
                </div>
                {/* Cards */}
                <div className="p-2 space-y-2 min-h-[200px] max-h-[70vh] overflow-y-auto">
                  <AnimatePresence>
                    {items.map(imp => (
                      <ImpegnoCard
                        key={imp.id}
                        impegno={imp}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onClick={() => openEdit(imp)}
                        onDelete={(e) => deleteImpegno(imp.id, e)}
                        isDragging={draggedId === imp.id}
                      />
                    ))}
                  </AnimatePresence>
                  {items.length === 0 && (
                    <div className="text-center text-xs text-slate-400 py-6 italic">
                      Trascina qui o crea un impegno
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DIALOG: Crea/Modifica */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Impegno' : 'Nuovo Impegno'}</DialogTitle>
            <DialogDescription>
              Compila i dati. La priorità determina il colore della card e l&apos;ordinamento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titolo *</Label>
              <Input value={form.titolo} onChange={e => setForm({ ...form, titolo: e.target.value })}
                placeholder="Es. Preparare manuale HACCP per Pasticceria X"
                data-testid="titolo-input" />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })}
                rows={2} placeholder="Dettagli del lavoro..." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Cliente / Azienda</Label>
                <select value={form.cliente_id || ''} onChange={e => onSelectCliente(e.target.value)}
                  className="w-full border rounded p-2 text-sm" data-testid="cliente-select">
                  <option value="">— Nessuno —</option>
                  {aziende.map(a => {
                    const id = a.id || a._id;
                    return <option key={id} value={id}>{a.denominazione || a.ragione_sociale}</option>;
                  })}
                </select>
              </div>
              <div>
                <Label>Tipo lavoro</Label>
                <Input value={form.tipo_lavoro} onChange={e => setForm({ ...form, tipo_lavoro: e.target.value })}
                  placeholder="Es. SCIA, Manuale HACCP, DVR"
                  list="tipi-lavoro" />
                <datalist id="tipi-lavoro">
                  <option value="Manuale HACCP" />
                  <option value="DVR" />
                  <option value="Pratica SCIA" />
                  <option value="Ispezione" />
                  <option value="Formazione" />
                  <option value="Audit" />
                  <option value="Preventivo" />
                  <option value="Fattura" />
                </datalist>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Priorità</Label>
                <select value={form.priorita} onChange={e => setForm({ ...form, priorita: e.target.value })}
                  className="w-full border rounded p-2 text-sm" data-testid="priorita-select">
                  {Object.entries(PRIORITA).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Inizio</Label>
                <Input type="date" value={form.data_inizio || ''} onChange={e => setForm({ ...form, data_inizio: e.target.value })} />
              </div>
              <div>
                <Label>Scadenza</Label>
                <Input type="date" value={form.data_scadenza || ''} onChange={e => setForm({ ...form, data_scadenza: e.target.value })}
                  data-testid="scadenza-input" />
              </div>
            </div>
            <div>
              <Label>Assegnato a</Label>
              <select value={form.assegnato_a_id || ''} onChange={e => onSelectAssegnato(e.target.value)}
                className="w-full border rounded p-2 text-sm" data-testid="assegnato-select">
                <option value="">— Me stesso —</option>
                {users.map(u => {
                  const id = u.id || u._id;
                  return <option key={id} value={id}>{u.full_name || u.email}</option>;
                })}
              </select>
            </div>
            <div>
              <Label>Tag</Label>
              <div className="flex items-center gap-2 mb-1">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="Aggiungi tag e premi Enter (es. urgente-cliente)"
                  className="text-sm" data-testid="tag-input" />
                <Button type="button" size="sm" variant="outline" onClick={addTag}>+</Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.tag.map(t => (
                  <Badge key={t} variant="outline" className="text-xs cursor-pointer" onClick={() => removeTag(t)}>
                    {t} <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                rows={2} placeholder="Note interne..." />
            </div>
          </div>
          <DialogFooter>
            {editingId && (
              <Button variant="ghost" onClick={() => deleteImpegno(editingId)} className="text-red-600 mr-auto">
                <Trash2 className="w-4 h-4 mr-1" /> Elimina
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Annulla</Button>
            <Button onClick={submit} disabled={saving || !form.titolo.trim()}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
              data-testid="submit-impegno">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {editingId ? 'Salva modifiche' : 'Crea impegno'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// === SUB-COMPONENTS ===
function StatCard({ icon: Icon, label, value, accent, pulse }) {
  return (
    <motion.div whileHover={{ y: -2 }} className={`bg-white border rounded-lg p-3 shadow-sm`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${accent} ${pulse ? 'animate-pulse' : ''}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-2xl font-bold leading-tight">{value}</p>
        </div>
      </div>
    </motion.div>
  );
}

function ImpegnoCard({ impegno, onDragStart, onDragEnd, onClick, onDelete, isDragging }) {
  const p = PRIORITA[impegno.priorita] || PRIORITA.media;
  const PIcon = p.icon;
  const oggi = new Date().toISOString().slice(0,10);
  const isScaduto = impegno.data_scadenza && impegno.data_scadenza < oggi && impegno.status !== 'done';
  const giorniDaScadenza = impegno.data_scadenza ?
    Math.round((new Date(impegno.data_scadenza) - new Date()) / (1000*60*60*24)) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: isDragging ? 0.3 : 1, scale: isDragging ? 0.95 : 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.15 }}
      draggable
      onDragStart={() => onDragStart(impegno.id)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      whileHover={{ y: -2, boxShadow: '0 8px 20px rgba(0,0,0,0.08)' }}
      className={`bg-white rounded-lg border-l-4 ${
        impegno.priorita === 'urgente' ? 'border-l-red-500' :
        impegno.priorita === 'alta' ? 'border-l-orange-500' :
        impegno.priorita === 'media' ? 'border-l-blue-500' :
        'border-l-emerald-500'
      } p-3 cursor-pointer shadow-sm hover:shadow-md transition-shadow`}
      data-testid={`impegno-${impegno.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <strong className="text-sm leading-snug flex-1">{impegno.titolo}</strong>
        <Badge className={`text-[10px] ${p.color} border whitespace-nowrap`}>
          <PIcon className="w-3 h-3 mr-0.5" /> {p.label}
        </Badge>
      </div>
      {impegno.cliente_nome && (
        <p className="text-xs text-slate-600 flex items-center gap-1 mb-1">
          <Building2 className="w-3 h-3" /> {impegno.cliente_nome}
        </p>
      )}
      {impegno.tipo_lavoro && (
        <p className="text-xs text-slate-500 italic">{impegno.tipo_lavoro}</p>
      )}
      {impegno.data_scadenza && (
        <div className={`text-xs mt-2 flex items-center gap-1 ${isScaduto ? 'text-red-600 font-bold' : giorniDaScadenza !== null && giorniDaScadenza <= 3 ? 'text-orange-600' : 'text-slate-500'}`}>
          <Clock className="w-3 h-3" />
          {isScaduto ? `⚠ SCADUTO ${Math.abs(giorniDaScadenza)}gg fa` :
           giorniDaScadenza === 0 ? '🔥 Scade oggi' :
           giorniDaScadenza !== null && giorniDaScadenza <= 7 ? `⏳ tra ${giorniDaScadenza}gg` :
           new Date(impegno.data_scadenza).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </div>
      )}
      {impegno.tag?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {impegno.tag.slice(0, 3).map(t => (
            <span key={t} className="text-[9px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">#{t}</span>
          ))}
        </div>
      )}
      {impegno.assegnato_a_nome && (
        <div className="flex items-center justify-between mt-2 pt-2 border-t">
          <div className="flex items-center gap-1 text-xs text-slate-600">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-400 to-indigo-500 text-white flex items-center justify-center text-[10px] font-bold">
              {(impegno.assegnato_a_nome[0] || '?').toUpperCase()}
            </div>
            <span className="truncate max-w-[100px]">{impegno.assegnato_a_nome}</span>
          </div>
          <button onClick={onDelete} className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </motion.div>
  );
}
